import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import UserHOC from "../../../components/shared/HOC/UserHOC.jsx";
import { Button, Typography, Modal, Space, Tooltip, Divider, Progress, Card, Spin, Empty } from "antd";
import UserCourseContentTable from "../../../components/shared/table/UserCourseContentTable.jsx";
import ContentShowPage from "./ContentShowPage.jsx";
import CustomButton from "../../../components/global/Button/CustomButton.jsx";
import CustomInput from "../../../components/global/CustomInput/CustomInput.jsx";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon.jsx";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import { useCourseContent } from "../../../hooks/UserHooks/MyCoursesHook/useCourseContent.js";
import "./CourseContentUser.css";
import CustomModal from "../../../components/global/CustomModal/CustomModal.jsx";
import PageLoader from "../../../components/global/Loader/PageLoader.jsx";

const { Title, Text } = Typography;

const CourseContent = ({ setHeader, setBreadCrumbs, setHideSidebar }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const quizResult = location.state?.quizResult;

  const {
    userId,
    loading,
    showContent,
    setShowContent,
    selectedRecord,
    setSelectedRecord,
    search,
    setSearch,
    currentPage,
    pageSize,
    totalElements,
    courseContent,
    courseTitle,
    quizAvailable,
    totalAttempts,
    allAcknowledged,
    handleTableChange,
    refreshCourseProgress,
    quizAttempted,
  } = useCourseContent(courseId);
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);

  useEffect(() => {
    if (quizResult) setShowResultModal(true);
  }, [quizResult]);

  useEffect(() => {
    setHideSidebar(showContent);
  }, [showContent, setHideSidebar]);

  const fetchDetailedAttempts = () => {
    const params = new URLSearchParams({
      userId: String(userId),
      courseId: String(courseId),
      courseName: courseTitle,
    });
    navigate(`/quiz-attempt-details?${params.toString()}`);
  };

  const closeResultModal = () => {
  setShowResultModal(false);
  navigate(location.pathname, { replace: true, state: {} });
};

  const isPassingScore = quizResult?.percentageScore >= (quizResult?.passingScore ?? 50);
  return (
    <div className="admin-section">
      {showContent ? (
        <ContentShowPage
          items={courseContent}
          selectedRecord={selectedRecord}
          setShowContent={setShowContent}
          setSelectedRecord={setSelectedRecord}
          courseId={courseId}
          loadCourseContent={refreshCourseProgress}
          quizAvailable={quizAvailable}
          userId={userId}
        />
      ) : (
        <Card className="course-content-card" bordered={true}>
          <div className="search-container-row">
            <div className="title-wrapper">
              <h2 className="course-title">{courseTitle}</h2>
            </div>

            <div className="user-action-buttons">
              <div
                className="search-box-wrapper"
                onMouseEnter={() => setIsInputHovered(true)}
                onMouseLeave={() => setIsInputHovered(false)}
              >
                <CustomInput
                  icon={
                    isInputHovered ? (
                      <LottieIcon name="searchIcon" size={24} shouldPlay />
                    ) : (
                      <img src={searchIconsvg} alt="search" style={{ width: 24 }} />
                    )
                  }
                  placeholder="Search by title"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  allowClear
                />
              </div>

              {quizAvailable && (
                <CustomButton
                  type="primary"
                  onClick={() => navigate(`/quiz/${courseId}?userId=${userId}`)}
                  disabled={!allAcknowledged}
                >
                  Attempt Quiz
                </CustomButton>
              )}

              <Tooltip title={quizAttempted === false ? "No attempts yet" : "View previous attempts"}>
                <CustomButton
                  disabled={quizAttempted === false}
                  onClick={fetchDetailedAttempts}
                  variant="outlined"
                >
                  View Attempts
                </CustomButton>
              </Tooltip>
            </div>
          </div>

          <div className="table-container">
            <PageLoader visible={loading} />
              {!loading && courseContent.length === 0 ? (
                <Empty description={search ? "No content found matching your search" : "No data available"} />
              ) : (
                <UserCourseContentTable
                  tableConfig={{
                    fields: [
                      { index: 1, title: "Title" },
                      { index: 2, title: "Description" },
                      { index: 4, title: "Resource Link" },
                    ],
                    entries: courseContent,
                    courseId: courseId,
                    onProgressUpdate: refreshCourseProgress,
                    loadCourseContent: refreshCourseProgress,
                    setShowContent: setShowContent,
                    setSelectedRecord: (record) => {
                      const link = record.resourceLink?.toLowerCase() || "";
                      const isYoutube = link.includes("youtube.com") || link.includes("youtu.be");
                      if (!isYoutube && (link.startsWith("http://") || link.startsWith("https://"))) {
                        window.open(record.resourceLink, "_blank");
                      } else {
                        setSelectedRecord(record);
                        setShowContent(true);
                      }
                    },
                    pagination: {
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalElements,
                      hideOnSinglePage: true,
                      showQuickJumper: true,
                    },
                    onChange: handleTableChange,
                  }}
                />
              )}
          </div>
        </Card>
      )}

      {quizResult && showResultModal && (
        <CustomModal
          visible={true}
          onClose={closeResultModal}
          title={isPassingScore ? "Congratulations! You Passed 🎉" : "Result Summary"}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Text strong>Correct Answers:</Text>{" "}
              <Text type="success">{quizResult.correctAnswers}</Text>
            </div>
            <div>
              <Text strong>Percentage Score:</Text>{" "}
              <Text type="secondary">{quizResult.percentageScore}%</Text>
            </div>
            <div>
              <Text strong>Total Score:</Text>{" "}
              <Text className="score-highlight">{quizResult.totalScore}</Text>
            </div>
            <Divider style={{ margin: "8px 0" }} />
            <Progress
              percent={quizResult.percentageScore}
              status={isPassingScore ? "success" : "exception"}
              strokeColor={isPassingScore ? "#088A15" : "#ff4d4f"}
              showInfo={true}
            />
          </Space>

          <div className="flex justify-end gap-3 mt-6">
            <CustomButton
              variant="outlined"
              onClick={() => closeResultModal()}
            >
              Back to Course
            </CustomButton>
            <CustomButton
              variant="primary"
              onClick={() => {
                closeResultModal();
                fetchDetailedAttempts();
              }}
            >
              View Detailed Result
            </CustomButton>
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default UserHOC(CourseContent);